import type { FC, PropsWithChildren, ReactNode } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import TimesIcon from '../../assets/TimesIcon.svg';
import ReactDOM from 'react-dom';
import { useEscapeKeyHandler } from '../../lib/hooks/useEscapeKeyHandler';

const modalRoot = document.getElementById('modal');

export const enum ModalSize {
  MEDIUM = 'medium',
  LARGE = 'large',
}

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  hideCloseButton?: boolean;
  zIndex?: number;
  clickOutsideClose?: boolean;
  escapeKeyClose?: boolean;
  disableBodyScroll?: boolean;
  size?: ModalSize;
} & PropsWithChildren;

export const Modal: FC<ModalProps> = ({
  title,
  isOpen,
  onClose,
  hideCloseButton,
  zIndex,
  children,
  clickOutsideClose,
  escapeKeyClose,
  disableBodyScroll,
  size,
}: ModalProps) => {
  const showDefaultModalHeader = title || !hideCloseButton;
  useEscapeKeyHandler({ active: !!escapeKeyClose, onEscapePressed: onClose });

  if (!isOpen || !modalRoot) {
    return null;
  }

  const ModalTemplate = (
    <>
      {disableBodyScroll && <PreventBodyScrollStyle />}
      <ModalOverlay zIndex={zIndex} onClick={() => clickOutsideClose && onClose()}>
        <ModalContainer onClick={(e) => e.stopPropagation()} size={size}>
          {showDefaultModalHeader && (
            <ModalHeader>
              {title && <Title>{title}</Title>}
              {!hideCloseButton && (
                <CloseButton onClick={onClose}>
                  <TimesIcon />
                </CloseButton>
              )}
            </ModalHeader>
          )}
          {children}
        </ModalContainer>
      </ModalOverlay>
    </>
  );

  return ReactDOM.createPortal(ModalTemplate, modalRoot);
};

const ModalOverlay = styled.div<{ zIndex?: number }>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.25);
  z-index: ${({ zIndex }) => (zIndex ? zIndex : 999)};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 16px 24px;
`;

const Title = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.prototype.foreground};
  text-transform: uppercase;
`;

const ModalContainer = styled.div<{ size?: ModalSize }>`
  position: relative;
  overflow-x: hidden;
  min-width: 388px;
  min-height: 185px;
  max-height: 95vh;
  max-width: 90vw;
  display: flex;
  border-radius: 16px;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.prototype.card};
  box-shadow: ${({ theme }) => theme.shadow.light};
  ${({ size }) =>
    size &&
    `
    width: ${size === ModalSize.MEDIUM ? '60%' : '90%'};
    height: ${size === ModalSize.MEDIUM ? 'auto' : '80%'};
  `}
`;

const CloseButton = styled.button`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  background: none;
  border: none;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  cursor: pointer;
`;

const PreventBodyScrollStyle = createGlobalStyle`
    body {
        overflow: hidden;
    }
`;
